/**
 * +personas: a persona's chat voice. Picks lines from the content pools (content/personas/lines.ts)
 * by archetype and tone, and rate-limits them by the chatty trait so bots are fun, not spammy:
 *   - each situation has a base chance (SITUATION_CHANCE), scaled by `talkiness(chatty)`;
 *   - at most one line per MIN_GAP_MS and BURST_MAX lines per BURST_WINDOW_MS per bot;
 *   - a line is "typed": it lands TYPE_MS_BASE + TYPE_MS_PER_CHAR × length later;
 *   - lines that explain the bot's own behaviour (brb, rage quit) skip the limits (`force`).
 * Deterministic: all picks use the persona's own seeded Rng.
 */
import type { ChatSituation, Persona } from '@keystone/content';
import { LINES, fillLine } from '@keystone/content';
import type { Ms, Rng } from '@keystone/sim';

/** Base chance (before talkiness) that a persona says something when the situation happens. */
export const SITUATION_CHANCE: Readonly<Record<ChatSituation, number>> = {
  run_start: 0.55,
  greet_human: 0.45,
  pull: 0.12,
  big_pull: 0.35,
  boss_pull: 0.35,
  boss_kill: 0.45,
  other_died: 0.3,
  self_died: 0.5,
  human_died: 0.35,
  wipe: 0.55,
  kick_landed: 0.1,
  kick_missed: 0.12,
  interrupt_call: 0.1,
  loot: 0.3,
  low_timer: 0.5,
  timed: 0.85,
  depleted: 0.8,
  afk_start: 0.9,
  afk_long: 0.6,
  afk_back: 0.75,
  wander_start: 0.85,
  wander_back: 0.5,
  tilt_1: 0.7,
  tilt_2: 0.85,
  rage_quit: 1,
  someone_left: 0.6,
  abandon: 0.8,
  afk_kick: 0.7,
  ambient: 1,
  reply: 0.35,
  waiting: 0.5,
};

/** Shortest gap between two of one bot's lines (unforced). */
export const MIN_GAP_MS: Ms = 4000;
/** At most BURST_MAX unforced lines per BURST_WINDOW_MS. */
export const BURST_MAX = 3;
export const BURST_WINDOW_MS: Ms = 45_000;
const TYPE_MS_BASE: Ms = 500;
const TYPE_MS_PER_CHAR: Ms = 45;
const TYPE_MS_MAX: Ms = 3500;
/** Remembered recent line ids (no immediate repeats). */
const RECENT_IDS = 8;

/** chatty 0..1 → multiplier on every situation chance (silent personas still say gg sometimes). */
export function talkiness(chatty: number): number {
  return 0.15 + 0.85 * Math.max(0, Math.min(1, chatty));
}

export interface PickedLine {
  text: string;
  lineId: string;
  emote: boolean;
}

export interface QueuedLine extends PickedLine {
  situation: ChatSituation;
  at: Ms;
}

export type LineVars = Readonly<Record<string, string | number | undefined>>;

/** Pick one line for `situation` in this persona's voice (null: the pool has nothing for it). */
export function pickLine(persona: Pick<Persona, 'archetype' | 'traits'>, situation: ChatSituation, vars: LineVars, rng: Rng, avoid?: ReadonlySet<string>): PickedLine | null {
  const pool = LINES[situation];
  const tone = persona.traits.toxicity > 0.5 ? 'salty' : 'kind';
  const buckets: [string, readonly string[], number][] = [];
  const own = pool[persona.archetype];
  if (own?.length) buckets.push([persona.archetype, own, 0.55]);
  const toned = pool[tone];
  if (toned?.length) buckets.push([tone, toned, tone === 'salty' ? 0.25 + 0.4 * persona.traits.toxicity : 0.25]);
  if (pool.any?.length) buckets.push(['any', pool.any, 0.3]);
  if (buckets.length === 0) return null;
  let best: PickedLine | null = null;
  for (let attempt = 0; attempt < 3; attempt++) {
    const total = buckets.reduce((s, b) => s + b[2], 0);
    let r = rng.next() * total;
    let pick = buckets[buckets.length - 1]!;
    for (const b of buckets) {
      r -= b[2];
      if (r < 0) {
        pick = b;
        break;
      }
    }
    const idx = rng.int(0, pick[1].length - 1);
    const raw = pick[1][idx]!;
    const emote = raw.length > 2 && raw.startsWith('*') && raw.endsWith('*');
    best = { text: fillLine(emote ? raw.slice(1, -1) : raw, vars), lineId: `${situation}.${pick[0]}.${idx}`, emote };
    if (!avoid?.has(best.lineId)) break;
  }
  return best;
}

export class ChatVoice {
  private lastAt: Ms = -Infinity;
  private recentTimes: Ms[] = [];
  private recentIds: string[] = [];
  private queue: QueuedLine[] = [];
  /** Lines offered / queued so far (tests, harness). */
  said = 0;

  constructor(
    private readonly persona: Pick<Persona, 'archetype' | 'traits'>,
    private readonly rng: Rng,
  ) {}

  /** Lines waiting to be typed. */
  get pending(): number {
    return this.queue.length;
  }

  /** May an unforced line be queued at `now` (rate limits only)? */
  canSpeak(now: Ms): boolean {
    if (now - this.lastAt < MIN_GAP_MS || this.queue.length > 0) return false;
    this.recentTimes = this.recentTimes.filter((t) => now - t < BURST_WINDOW_MS);
    return this.recentTimes.length < BURST_MAX;
  }

  /**
   * Maybe say something about `situation`: rolls SITUATION_CHANCE × talkiness × `boost` (forced
   * lines skip the roll and the rate limits). Returns true when a line was queued.
   */
  offer(situation: ChatSituation, now: Ms, vars: LineVars, opts: { force?: boolean; boost?: number; delayMs?: Ms } = {}): boolean {
    if (!opts.force) {
      if (!this.canSpeak(now)) return false;
      const p = Math.min(1, SITUATION_CHANCE[situation] * talkiness(this.persona.traits.chatty) * (opts.boost ?? 1));
      if (!this.rng.chance(p)) return false;
    }
    const line = pickLine(this.persona, situation, vars, this.rng, new Set(this.recentIds));
    if (!line) return false;
    const typing = Math.min(TYPE_MS_MAX, TYPE_MS_BASE + TYPE_MS_PER_CHAR * line.text.length);
    this.queue.push({ ...line, situation, at: now + (opts.delayMs ?? 0) + typing });
    this.queue.sort((a, b) => a.at - b.at);
    this.lastAt = now;
    this.recentTimes.push(now);
    this.recentIds.push(line.lineId);
    if (this.recentIds.length > RECENT_IDS) this.recentIds.shift();
    this.said++;
    return true;
  }

  /** Lines whose typing finished by `now` (removed from the queue). */
  due(now: Ms): QueuedLine[] {
    if (this.queue.length === 0 || this.queue[0]!.at > now) return [];
    const out: QueuedLine[] = [];
    while (this.queue.length > 0 && this.queue[0]!.at <= now) out.push(this.queue.shift()!);
    return out;
  }

  /** Drop queued lines (the speaker left, or a line went stale). */
  clear(): void {
    this.queue = [];
  }
}
